import {ReactNode, SVGProps, useEffect, useRef} from 'react';
import {useSettings} from '@common/core/settings/use-settings';
import {Link, useLocation} from 'react-router-dom';

export const c = {
  bg: '#FFFFFF',
  ink: '#071033',
  ink2: '#1E2A52',
  inkMuted: '#4A5578',
  inkSoft: '#8A93B8',
  rule: 'rgba(10, 63, 197, 0.14)',
  ruleSoft: '#F2F6FF',
  lime: '#C8F15A',
  limeDeep: '#0A3FC5',
  limeSoft: 'rgba(0, 200, 255, 0.12)',
  navy: '#050B26',
  cyan: '#00C8FF',
  cyanDark: '#0A3FC5',
  green: '#1FD3C6',
};

// landing routes render as SPA navigations, which keep the previous page's
// scroll position; without this, pages open scrolled to the middle
export function useScrollToTopOnMount() {
  const {hash} = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
    }
    // only on mount; hash deep-links keep native anchor behavior
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export function Container({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`mx-auto w-full max-w-1280 px-24 md:px-40 ${className}`}>
      {children}
    </div>
  );
}

export function SiteHeader() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      if (window.scrollY > 8) el.setAttribute('data-scrolled', 'true');
      else el.removeAttribute('data-scrolled');
    };
    onScroll();
    window.addEventListener('scroll', onScroll, {passive: true});
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header ref={ref} className="mrigl-public-header sticky top-0 z-50">
      <div className="mrigl-top-banner">
        Offre de lancement : <span>créez votre site gratuitement</span> — aucun
        paiement demandé.
      </div>
      <Container className="mrigl-nav-inner flex items-center justify-between gap-16 py-16">
        <Wordmark />
        <nav
          className="mrigl-nav-links hidden items-center gap-10 md:flex"
          aria-label="Navigation"
        >
          <HeaderHashLink hash="modeles">Modèles</HeaderHashLink>
          <HeaderOfferLink />
          <HeaderHashLink hash="faq">Questions</HeaderHashLink>
          <Link to="/contact" className="header-link text-sm">
            Contact
          </Link>
        </nav>
        <div className="mrigl-nav-actions flex items-center gap-10">
          <Link
            to="/contact"
            className="nav-feedback inline-flex items-center justify-center gap-6 rounded px-14 py-8 text-sm font-bold"
            aria-label="Envoyer un feedback"
          >
            <FeedbackBubble />
            <span className="nav-feedback-label">Feedback</span>
          </Link>
          <Link
            to="/login"
            className="nav-cta nav-login inline-flex items-center justify-center rounded px-16 py-8 text-sm font-bold"
          >
            Connexion
          </Link>
          <Link
            to="/register"
            className="nav-cta-primary hidden items-center justify-center rounded px-18 py-10 text-sm font-extrabold sm:inline-flex"
          >
            Créer gratuitement
          </Link>
        </div>
      </Container>
    </header>
  );
}

function HeaderHashLink({hash, children}: {hash: string; children: ReactNode}) {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const href = `#${hash}`;

  if (isHome) {
    return (
      <a href={href} className="header-link text-sm">
        {children}
      </a>
    );
  }

  return (
    <Link to={`/${href}`} className="header-link text-sm">
      {children}
    </Link>
  );
}

function HeaderOfferLink() {
  const location = useLocation();
  const isHome = location.pathname === '/';

  if (isHome) {
    // Same-page hash navigation uses native browser scroll.
    return (
      <a href="#creer" className="header-link text-sm">
        Offre
      </a>
    );
  }
  return (
    <Link to="/#creer" className="header-link text-sm">
      Offre
    </Link>
  );
}

export function Wordmark() {
  const {branding} = useSettings();
  const logo = branding?.logo_dark || branding?.logo_light;

  if (logo) {
    return (
      <Link
        to="/"
        aria-label="MRIGL.site, accueil"
        className="mrigl-wordmark inline-flex"
      >
        <img
          src={logo}
          alt={branding?.site_name || 'MRIGL.site'}
          className="h-52 max-h-52 w-auto"
        />
      </Link>
    );
  }

  return (
    <Link
      to="/"
      aria-label="MRIGL.site, accueil"
      className="mrigl-wordmark inline-flex items-baseline text-2xl"
      style={{color: c.ink, fontWeight: 800, letterSpacing: '-0.01em'}}
    >
      mrigl
      <span style={{color: c.cyanDark}}>.</span>
      <span style={{color: c.inkMuted, fontWeight: 600}}>site</span>
    </Link>
  );
}

export function SiteFooter() {
  return (
    <footer className="mrigl-public-footer border-t py-42">
      <Container>
        <div className="footer-inner flex flex-wrap items-center justify-between gap-x-32 gap-y-16">
          <div className="flex flex-wrap items-center gap-x-20 gap-y-8">
            <Wordmark />
            <span className="text-sm" style={{color: c.inkMuted}}>
              Des sites simples, gratuits pendant le lancement.
            </span>
          </div>
          <nav
            className="flex flex-wrap gap-x-24 gap-y-8"
            aria-label="Liens secondaires"
          >
            <FooterLink href="/pages/privacy-policy">
              Politique de confidentialité
            </FooterLink>
            <FooterLink href="/pages/terms-of-service">
              Conditions d'utilisation
            </FooterLink>
            <FooterLink href="/contact">Nous contacter</FooterLink>
            <FooterLink href="/contact">Feedback</FooterLink>
          </nav>
        </div>
        <nav
          className="mt-20 flex flex-wrap gap-x-24 gap-y-8 border-t pt-20"
          style={{borderColor: c.rule}}
          aria-label="Guides"
        >
          <span className="text-sm font-semibold" style={{color: c.ink}}>
            Guides :
          </span>
          <FooterLink href="/pages/creer-site-web-tunisie">
            Créer un site web en Tunisie
          </FooterLink>
          <FooterLink href="/pages/prix-site-web-tunisie">
            Prix d'un site web
          </FooterLink>
          <FooterLink href="/pages/site-web-sans-coder-tunisie">
            Créer un site sans coder
          </FooterLink>
          <FooterLink href="/pages/site-web-restaurant-cafe-tunisie">
            Site pour restaurant
          </FooterLink>
          <FooterLink href="/pages/site-web-freelance-tunisie">
            Site pour freelance
          </FooterLink>
        </nav>
      </Container>
    </footer>
  );
}

function FeedbackBubble() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="17"
      height="17"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
      <path d="M8 9h8M8 13h5" />
    </svg>
  );
}

function FooterLink({href, children}: {href: string; children: ReactNode}) {
  return (
    <a
      href={href}
      className="text-sm transition-colors"
      style={{color: c.inkMuted, textDecoration: 'none'}}
      onMouseEnter={e => (e.currentTarget.style.color = c.ink)}
      onMouseLeave={e => (e.currentTarget.style.color = c.inkMuted)}
    >
      {children}
    </a>
  );
}

export function Reveal({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (
      typeof IntersectionObserver === 'undefined' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      el.classList.add('in');
      return;
    }
    const io = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          // also reveal content sitting above the viewport: after SPA
          // navigation the scroll position is preserved, so sections
          // "scrolled past" would otherwise stay invisible forever
          if (e.isIntersecting || e.boundingClientRect.top < 0) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      },
      {rootMargin: '0px 0px -8% 0px', threshold: 0.08},
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className={`reveal ${className}`}>
      {children}
    </div>
  );
}

export function ArrowRight(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
      {...props}
    >
      <path
        d="M2 7h10M8 3l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function CheckMark(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
      {...props}
    >
      <path
        d="M2.5 7.5l3 3 6-6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
